// The operator dropdowns: which operators a column type offers, in what ORDER, and what value-control
// each one needs.
//
// WHICH operators is the backend's answer — `OPERATORS_BY_TYPE` in the audience registry, the same
// table `operator-fit.ts` validates a saved definition against. The LABELS are the backend's too, since
// ACT2-600 — `DEFAULT_OPERATOR_LABELS` plus `OPERATOR_LABEL_OVERRIDES` in the same registry, read
// through `operatorLabel`, because the SQL comment writer needs the same
// English the dropdown shows and a label written twice is a label that drifts. This file supplies what
// is genuinely the editor's: the dropdown ORDER, and each operator's {@link ValueShape}.
//
// `presentableFor` FILTERS to the backend's set, so the menu is always a SUBSET of what a save accepts —
// which is the direction that matters: the editor can never offer a rule `saveAudience` would refuse.
// The reverse is deliberate: the backend accepts rules this editor has no control for (a list of dates),
// which the agent can author and the clause renders read-only. `operators.test.ts` pins the direction.

import { booleanPillSpelling } from '@server/audience-boolean-pill';
import { operatorLabel } from '@server/audience-operator-labels';
import { OPERATORS_BY_TYPE } from '@server/audience-operators';

import type {
  AttributeType,
  ConditionValue,
  OperatorDef,
  OperatorValue,
  RelativeWindow,
  TimeUnit,
  ValueShape,
} from '../types';

/** Value-control per operator, per type, in the order the dropdown should read. The SET of operators
 *  and the LABEL for each are both the backend's — see the module header. */
const PRESENTATION: Record<AttributeType, { value: OperatorValue; shape: ValueShape }[]> = {
  string: [
    { value: 'eq', shape: 'single_enum' },
    { value: 'neq', shape: 'single_enum' },
    { value: 'like', shape: 'text' },
    { value: 'nlike', shape: 'text' },
    { value: 'in', shape: 'multi_enum' },
    { value: 'nin', shape: 'multi_enum' },
    { value: 'nnull', shape: 'none' },
    { value: 'null', shape: 'none' },
  ],
  number: [
    { value: 'eq', shape: 'number' },
    { value: 'neq', shape: 'number' },
    { value: 'gt', shape: 'number' },
    { value: 'gte', shape: 'number' },
    { value: 'lt', shape: 'number' },
    { value: 'lte', shape: 'number' },
    { value: 'in', shape: 'multi_enum' }, // numeric membership
    { value: 'nin', shape: 'multi_enum' },
    { value: 'btw', shape: 'number_range' },
    { value: 'nnull', shape: 'none' },
    { value: 'null', shape: 'none' },
  ],
  boolean: [
    { value: 'eq', shape: 'boolean' },
    { value: 'neq', shape: 'boolean' },
    { value: 'nnull', shape: 'none' },
    { value: 'null', shape: 'none' },
  ],
  timestamp: [
    { value: 'inlast', shape: 'relative_window' },
    { value: 'notinlast', shape: 'relative_window' },
    { value: 'innext', shape: 'relative_window' },
    { value: 'btw', shape: 'date_range' },
    { value: 'eq', shape: 'date' },
    { value: 'neq', shape: 'date' },
    { value: 'lt', shape: 'date' },
    { value: 'lte', shape: 'date' },
    { value: 'gt', shape: 'date' },
    { value: 'gte', shape: 'date' },
    { value: 'nnull', shape: 'none' },
    { value: 'null', shape: 'none' },
  ],
};

/**
 * The dropdowns. Every entry is an operator the BACKEND says the type accepts — `PRESENTATION` only
 * decides how it reads and what control it needs, and an operator it has no entry for is dropped rather
 * than rendered label-less. Drift in either direction is visible: a backend addition disappears from
 * the menu until a label is written, which `operators.test.ts` asserts against the backend table.
 */
export const ATTRIBUTE_OPERATORS: Record<AttributeType, OperatorDef[]> = {
  string: presentableFor('string'),
  number: presentableFor('number'),
  boolean: presentableFor('boolean'),
  timestamp: presentableFor('timestamp'),
};

function presentableFor(type: AttributeType): OperatorDef[] {
  const allowed = new Set<string>(OPERATORS_BY_TYPE[type]);
  return PRESENTATION[type]
    .filter((def) => allowed.has(def.value))
    .map((def) => ({ ...def, label: operatorLabel(def.value, type) }));
}

export const TIME_UNITS: { value: TimeUnit; label: string }[] = [
  { value: 'days', label: 'days' },
  { value: 'weeks', label: 'weeks' },
  { value: 'months', label: 'months' },
];

export function findOperator(operators: OperatorDef[], value: string): OperatorDef | undefined {
  return operators.find((o) => o.value === value);
}

/** Whether `operator` is one this column type offers — the editable-vs-read-only decision for a leaf. */
export function isOperatorAllowed(type: AttributeType, operator: OperatorValue): boolean {
  return !!findOperator(ATTRIBUTE_OPERATORS[type], operator);
}

/** The first OperatorDef for this operator across all type sets — a type-free lookup for the read-only
 *  fallback, so a valid operator still gets a human label + value shape when the column type is unknown
 *  or doesn't offer it (labels are per-type; this picks the first, which is a sensible generic). */
export function operatorDef(operator: string): OperatorDef | undefined {
  for (const set of Object.values(ATTRIBUTE_OPERATORS)) {
    const def = findOperator(set, operator);
    if (def) return def;
  }
  return undefined;
}

export function operatorShape(operators: OperatorDef[], value: string): ValueShape {
  return findOperator(operators, value)?.shape ?? 'none';
}

/** The empty value for a shape — what a control shows before anything is entered. */
export function defaultForShape(shape: ValueShape): ConditionValue {
  switch (shape) {
    case 'text':
      return '';
    case 'multi_enum':
      return [];
    case 'boolean':
      return true;
    case 'number_range':
      return [null, null] as [number | null, number | null];
    case 'date_range':
      return [null, null] as [string | null, string | null];
    case 'relative_window':
      return null; // unset — a fresh relative window starts blank, not a phantom "30 days"
    case 'single_enum':
    case 'number':
    case 'date':
    case 'none':
      return null;
  }
}

/** Seed the value when the operator changes: keep it if the shape is unchanged, coerce across the
 *  trivially-compatible pairs (single↔multi, number↔range, date↔date_range), else the shape's default. */
export function seedValueForShape(next: ValueShape, prev: ConditionValue, prevShape: ValueShape): ConditionValue {
  if (next === prevShape) return prev;
  if (next === 'multi_enum' && prevShape === 'single_enum') return typeof prev === 'string' && prev ? [prev] : [];
  if (next === 'single_enum' && prevShape === 'multi_enum')
    return Array.isArray(prev) && typeof prev[0] === 'string' ? prev[0] : null;
  if (next === 'number_range' && prevShape === 'number') return [typeof prev === 'number' ? prev : null, null];
  if (next === 'number' && prevShape === 'number_range')
    return Array.isArray(prev) && typeof prev[0] === 'number' ? prev[0] : null;
  if (next === 'date_range' && prevShape === 'date') return [typeof prev === 'string' ? prev : null, null];
  if (next === 'date' && prevShape === 'date_range')
    return Array.isArray(prev) && typeof prev[0] === 'string' ? prev[0] : null;
  return defaultForShape(next);
}

// Narrow the bare value BY SHAPE — the operator's shape is the discriminator, so these trust it and
// fall back safely for the transient frame right after an operator switch.
export const asText = (v: ConditionValue): string => (typeof v === 'string' ? v : '');
export const asEnumSingle = (v: ConditionValue): string | undefined => (typeof v === 'string' ? v : undefined);
export const asEnumMulti = (v: ConditionValue): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
// Numeric membership (`in`/`nin` on a number column) — preserves `number[]` rather than dropping to strings.
export const asEnumMultiNumber = (v: ConditionValue): number[] =>
  Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number') : [];
export const asNumber = (v: ConditionValue): number | null => (typeof v === 'number' ? v : null);
export const asNumberRange = (v: ConditionValue): [number | null, number | null] =>
  Array.isArray(v) ? [typeof v[0] === 'number' ? v[0] : null, typeof v[1] === 'number' ? v[1] : null] : [null, null];
export const asBoolean = (v: ConditionValue): boolean | undefined => (typeof v === 'boolean' ? v : undefined);
// The predicate's JSON boolean → the spelling its pill carries. BOTH halves of the round trip live in
// `@server/audience-boolean-pill` and are imported rather than restated here: the pills are BUILT on
// the backend from `topValues`, so a spelling written twice is a spelling that can drift, and the
// drift is silent in both directions — nothing reads as selected, and a click writes a value the
// compiler treats as a string (`= 'true'` compiles, runs, and matches no rows). Decode with
// `booleanFromPill` from that same module; it returns `undefined` for a spelling it does not
// recognise, which the caller must DECLINE rather than commit (ACT2-558).
export const asBooleanPill = (v: ConditionValue): string | undefined => booleanPillSpelling(asBoolean(v));
export const asDate = (v: ConditionValue): string | null => (typeof v === 'string' ? v : null);
export const asDateRange = (v: ConditionValue): [string | null, string | null] =>
  Array.isArray(v) ? [typeof v[0] === 'string' ? v[0] : null, typeof v[1] === 'string' ? v[1] : null] : [null, null];
// A window value, or `null` when unset — never fabricate a phantom `{30, days}`: a null window reads as
// "…", and a unit-only edit on an empty window can't accidentally commit a phantom interval.
export const asWindow = (v: ConditionValue): RelativeWindow | null =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? v : null;
